import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { 
  Container, 
  Paper, 
  Typography, 
  Box, 
  CircularProgress, 
  Alert,
  Button,
  Link,
  Card,
  CardContent
} from '@mui/material';
import { 
  GetApp as GetAppIcon, 
  Phone as PhoneIcon,
  Launch as LaunchIcon 
} from '@mui/icons-material';
import Layout from '../components/layout/Layout';

interface InvitationData {
  id: string;
  title: string;
  description: string;
  expiresAt: Date;
  isValid: boolean;
}

interface DeepLinkState {
  attempting: boolean;
  attempted: boolean;
  success: boolean;
  timeElapsed: number;
}

const InvitePage: React.FC = () => {
  const { invitationId } = useParams<{ invitationId: string }>();
  const [invitation, setInvitation] = useState<InvitationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deepLinkState, setDeepLinkState] = useState<DeepLinkState>({
    attempting: false,
    attempted: false,
    success: false,
    timeElapsed: 0
  });
  const [deviceInfo] = useState({
    isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
    isAndroid: /Android/.test(navigator.userAgent),
    isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  });

  // Auto deep link attempt function
  const attemptDeepLink = () => {
    if (!invitationId || deepLinkState.attempted) return;

    const customSchemeUrl = `spendifly://invite/${invitationId}`;
    const startTime = Date.now();

    setDeepLinkState(prev => ({ ...prev, attempting: true }));

    try {
      // Create hidden iframe for iOS compatibility
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = customSchemeUrl;
      document.body.appendChild(iframe);

      // Also try direct window.location for Android
      setTimeout(() => {
        window.location.href = customSchemeUrl;
      }, 100);

      // Check if app opened after 800ms
      setTimeout(() => {
        const elapsed = Date.now() - startTime;
        const success = document.hidden || elapsed > 1200;
        
        setDeepLinkState(prev => ({
          ...prev,
          attempting: false,
          attempted: true,
          success,
          timeElapsed: elapsed
        }));

        // Clean up iframe
        try {
          document.body.removeChild(iframe);
        } catch (e) {
          // Iframe might already be removed
        }
      }, 800);

    } catch (err) {
      console.error('Deep link error:', err);
      setDeepLinkState(prev => ({
        ...prev,
        attempting: false,
        attempted: true,
        success: false,
        timeElapsed: Date.now() - startTime
      }));
    }
  };

  useEffect(() => {
    const fetchInvitation = async () => {
      if (!invitationId) {
        setError('Invalid invitation ID');
        setLoading(false);
        return;
      }

      try {
        // TODO: Replace this with actual API call to fetch invitation data
        // Example: const response = await fetch(`/api/invitations/${invitationId}`);
        
        // Simulated API response for demonstration
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate network delay
        
        // Mock invitation data - replace with actual API call
        const mockInvitation: InvitationData = {
          id: invitationId,
          title: 'You\'re Invited to Spendifly!',
          description: `Join us on Spendifly with invitation: ${invitationId}`,
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
          isValid: true
        };

        setInvitation(mockInvitation);
        
        // Auto-attempt deep link after invitation loads
        setTimeout(attemptDeepLink, 500);
        
      } catch (err) {
        setError('Failed to load invitation');
        console.error('Error fetching invitation:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInvitation();
  }, [invitationId]);

  // Listen for page visibility changes to detect app opening
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && deepLinkState.attempting) {
        setDeepLinkState(prev => ({ ...prev, success: true }));
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [deepLinkState.attempting]);

  // If no invitation ID is provided, redirect to home
  if (!invitationId) {
    return <Navigate to="/" replace />;
  }

  const getAppStoreUrl = () => {
    if (deviceInfo.isIOS) {
      return 'https://apps.apple.com/app/spendifly/id123456789'; // Replace with actual App Store URL
    } else if (deviceInfo.isAndroid) {
      return 'https://play.google.com/store/apps/details?id=com.spendifly.app'; // Replace with actual Play Store URL
    }
    return null;
  };

  const retryDeepLink = () => {
    setDeepLinkState({
      attempting: false,
      attempted: false,
      success: false,
      timeElapsed: 0
    });
    setTimeout(attemptDeepLink, 100);
  };

  return (
    <Layout>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
              <Typography variant="body1" sx={{ ml: 2 }}>
                Loading invitation...
              </Typography>
            </Box>
          ) : error ? (
            <Box textAlign="center">
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
              <Button variant="contained" href="/">
                Go to Home
              </Button>
            </Box>
          ) : invitation ? (
            <Box>
              <Typography variant="h3" component="h1" gutterBottom textAlign="center">
                {invitation.title}
              </Typography>
              
              <Typography variant="body1" paragraph textAlign="center" color="text.secondary">
                {invitation.description}
              </Typography>

              {/* Deep Link Status Card */}
              <Card sx={{ mt: 4, mb: 3, bgcolor: deepLinkState.attempting ? '#e3f2fd' : deepLinkState.success ? '#e8f5e8' : deepLinkState.attempted ? '#fff3e0' : 'background.paper' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="center" sx={{ mb: 2 }}>
                    {deepLinkState.attempting ? (
                      <>
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography variant="h6" color="primary">
                          🚀 Attempting to open Spendifly app...
                        </Typography>
                      </>
                    ) : deepLinkState.success ? (
                      <Typography variant="h6" color="success.main">
                        ✅ App should be opening now!
                      </Typography>
                    ) : deepLinkState.attempted ? (
                      <Typography variant="h6" color="warning.main">
                        📱 App not detected - Download Spendifly below
                      </Typography>
                    ) : (
                      <Typography variant="h6">
                        🔗 Preparing to open app...
                      </Typography>
                    )}
                  </Box>

                  {deepLinkState.attempted && !deepLinkState.success && (
                    <Box textAlign="center" sx={{ mt: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Don't have Spendifly installed? Download it now to accept your invitation!
                      </Typography>
                      
                      <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                        {deviceInfo.isIOS && (
                          <Button
                            variant="contained"
                            size="large"
                            startIcon={<GetAppIcon />}
                            href={getAppStoreUrl()!}
                            target="_blank"
                            sx={{ bgcolor: '#000', '&:hover': { bgcolor: '#333' } }}
                          >
                            Download on App Store
                          </Button>
                        )}
                        
                        {deviceInfo.isAndroid && (
                          <Button
                            variant="contained"
                            size="large"
                            startIcon={<GetAppIcon />}
                            href={getAppStoreUrl()!}
                            target="_blank"
                            sx={{ bgcolor: '#01875f', '&:hover': { bgcolor: '#006144' } }}
                          >
                            Get it on Google Play
                          </Button>
                        )}
                        
                        {!deviceInfo.isMobile && (
                          <Box textAlign="center">
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              Open this link on your mobile device to download Spendifly:
                            </Typography>
                            <Typography variant="body1" sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 1, borderRadius: 1 }}>
                              gamiflytech.com/spendifly/invite/{invitationId}
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ mt: 3 }}>
                        <Button
                          variant="outlined"
                          onClick={retryDeepLink}
                          startIcon={<LaunchIcon />}
                        >
                          Try Opening App Again
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Invitation Details */}
              <Box sx={{ mt: 4, mb: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Invitation ID: {invitation.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Expires: {invitation.expiresAt.toLocaleDateString()}
                </Typography>
              </Box>

              {!invitation.isValid && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This invitation has expired or is no longer valid.
                </Alert>
              )}

              {/* Alternative Actions */}
              <Box textAlign="center" sx={{ mt: 4 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Having trouble? You can also:
                </Typography>
                
                <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                  <Button
                    variant="text"
                    size="small"
                    href="mailto:support@spendifly.com?subject=Invitation Help"
                  >
                    Contact Support
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    href="/"
                  >
                    Visit Website
                  </Button>
                </Box>
              </Box>
            </Box>
          ) : null}
        </Paper>
      </Container>
    </Layout>
  );
};

export default InvitePage; 